import _ from 'lodash';
import { titleCase } from '../../../components/base/common/utils';
import { Context } from '../index';

const domainRegex = /^(\*(\.)?)?(([a-zA-Z0-9]+[_-])*[a-zA-Z0-9]+)\.[a-zA-Z0-9-_.-]+$/;

export const filterParams = (
  context: Context,
  { params, endpointType }: { params: any; endpointType: any },
) => {
  const genericParams = ['full_name', 'short_name', 'url'];
  const sharedEditParams = [
    genericParams.slice(), // TODO: spread operator getting tslib error
    'affiliate_config',
    'is_pilot',
    'logo_url',
    'notes',
    'show_in_dashboard',
    'widget_options',
    'slug',
  ];
  const endpointParams = {
    '/publisher/id': [
      sharedEditParams.slice(), // TODO: spread operator getting tslib error
      'amazon_tracking_id',
      'cogs',
      'has_jstag',
      'reserve_cpc',
      'publisher_type',
      'terms_accepted',
      'user_terms_accepted',
      'datetime_terms_accepted',
    ],
    '/org': [genericParams.slice(), 'publisher_type'], // TODO: spread operator getting tslib error
    '/merchant/id': [
      sharedEditParams.slice(), // TODO: spread operator getting tslib error
      'ftp_account_name',
      'ftp_account_password',
      'expected_import_frequency',
      'impression_tracker',
      'click_tracker',
      'utm_params',
      'share_goal',
      'is_pacing_enabled',
      'pricing_model',
      'max_cpc',
      'cpc_goal',
      'roi_goal',
      'commission_rate',
      'is_active',
      'is_feed_importer_enabled',
    ],
    '/billing/customers': ['merch_id', 'email', 'invoicing_enabled', 'campaign_invoicing_enabled'],
  };

  return params
    ? Object.keys(params).reduce(
        (acc, curr) =>
          endpointParams[endpointType].includes(curr) ? { ...acc, [curr]: params[curr] } : acc, // TODO: spread operator getting tslib error
        {},
      )
    : null;
};

export const getAccount = (
  context: Context,
  { accountType, accountId }: { accountType: any; accountId: any },
) => {
  return context.effects.api.get(`/api/v0/${accountType}s/${accountId}/`);
};

export const getAccountUsers = (
  context: Context,
  { accountType, accountId }: { accountType: any; accountId: any },
) => {
  return context.effects.api.get(`/api/v0/admins/${accountType}_info/${accountId}`);
};

export const getPublisherAccounts = (context: Context) => {
  const endpoint = '/api/v0/publishers/';
  return context.effects.api.get(endpoint);
};

export const getMerchantAccounts = (context: Context) => {
  const endpoint = '/api/v0/merchants/';
  return context.effects.api.get(endpoint);
};

export const createAccount = (
  context: Context,
  { params, accountType }: { params: any; accountType: any },
) => {
  const endpoint = `/api/v0/${accountType}s/`;
  const filteredParams = context.actions.account.filterParams({
    params,
    endpointType: '/org',
  });

  const { firstErrorMsg, newParams, fieldErrors } = context.actions.account.validateParams({
    params: filteredParams,
  });
  if (firstErrorMsg) {
    return Promise.reject([new Error(firstErrorMsg), fieldErrors]);
  }
  return context.effects.api.post(endpoint, newParams);
};

export const saveAccountSlug = (
  context: Context,
  { params, accountType, accountId }: { params: any; accountType: any; accountId: any },
) => {
  const endpoint = `/api/v0/${accountType}s/${accountId}/`;
  const filteredParams = context.actions.account.filterParams({
    params,
    endpointType: `/${accountType}/id`,
  });
  const { firstErrorMsg, newParams, fieldErrors } = context.actions.account.validateParams({
    params: filteredParams,
    accountType,
  });
  if (firstErrorMsg) {
    return Promise.reject([new Error(firstErrorMsg), fieldErrors]);
  }
  return context.effects.api.put(endpoint, newParams);
};

export const editAccount = (
  context: Context,
  {
    params,
    accountType,
    accountId,
    affiliateConfigArgs,
  }: {
    params: any;
    accountType: any;
    accountId: any;
    affiliateConfigArgs: any;
  },
) => {
  const endpoint = `/api/v0/${accountType}s/${accountId}/`;
  const filteredParams = context.actions.account.filterParams({
    params,
    endpointType: `/${accountType}/id`,
  });
  const { firstErrorMsg, newParams, fieldErrors } = context.actions.account.validateParams({
    params: filteredParams,
    accountType,
  });
  const [
    linkMateErrors,
    validatedAffiliateConfig,
  ] = context.actions.account.validateAffiliateConfig({ affiliateConfigArgs, fieldErrors });
  const raiseError = firstErrorMsg || linkMateErrors;
  if (raiseError) {
    return Promise.reject([new Error(raiseError), fieldErrors]);
  }
  const paramsWithLinkConfig = {
    ...newParams,
    affiliate_config: { ...newParams.affiliate_config, ...validatedAffiliateConfig },
  };
  return context.effects.api.put(endpoint, paramsWithLinkConfig);
};

export const createUser = (context: Context, { params }: { params: any }) => {
  const { firstErrorMsg, newParams, fieldErrors } = context.actions.account.validateParams({
    params,
  });
  if (firstErrorMsg) {
    return Promise.reject([new Error(firstErrorMsg), fieldErrors]);
  }
  return context.effects.api.post('/api/v0/users/', newParams);
};

export const editUser = (context: Context, { params, id }: { params: any; id: any }) => {
  const { firstErrorMsg, newParams, fieldErrors } = context.actions.account.validateParams({
    params,
  });
  if (firstErrorMsg) {
    return Promise.reject([new Error(firstErrorMsg), fieldErrors]);
  }
  return context.effects.api.put(`/api/v0/users/${id}/`, newParams);
};

export const saveMerchantBilling = (
  context: Context,
  { params, stripeId }: { params: any; stripeId: any },
) => {
  const filteredParams = context.actions.account.filterParams({
    params,
    endpointType: '/billing/customers',
  });
  const { newParams, fieldErrors } = context.actions.account.validateParams({
    params: filteredParams,
  });
  const validEmail = /^.+@.+\..+$/.test(params.email);
  if (validEmail) {
    if (stripeId) {
      return context.effects.api.put(`/api/v0/billing/customers/${stripeId}/`, newParams);
    }
    return context.effects.api.post('/api/v0/billing/customers/', newParams);
  }
  return Promise.reject([
    new Error('Please use a valid Invoice Recipient email address'),
    ['invoice_recipient'],
  ]);
};

// export const validateParams: (
//   context: Context: { state: any; actions: any; effects: any },
//   { params, accountType }: { params: any; accountType: any },
// ) => { firstErrorMsg: any; newParams: any; fieldErrors: any } = (
//   context: Context,
//   { params, accountType },
// ): { firstErrorMsg: any; newParams: any; fieldErrors: any } => {
export const validateParams = (
  context: Context,
  { params, accountType }: { params: any; accountType?: any },
) => {
  const newParams: any = _.cloneDeep(params);
  const fieldErrors: any[] = [];
  let firstErrorMsg = '';
  const validators = [
    context.actions.account.checkNonEmpty,
    context.actions.account.checkLength,
    ...(accountType === 'publisher' ? [context.actions.account.checkDomains] : []),
  ];

  validators.forEach(validate => {
    if (!firstErrorMsg && newParams) {
      Object.entries(newParams).forEach(([param, value]) => {
        let errorMsg;
        if (validate === context.actions.account.checkDomains) {
          if (param === 'widget_options') {
            errorMsg = validate({ param: context.actions.account, value: (value as any).domains });
          }
        } else {
          errorMsg = validate({ param, value });
        }

        // Only highlight one field if there's a length error
        if (errorMsg && (!firstErrorMsg || validate !== context.actions.account.checkLength)) {
          fieldErrors.push(param);
          firstErrorMsg = errorMsg;
        }
      });
    }
  });

  if (newParams && newParams.widget_options && newParams.widget_options.domains) {
    newParams.widget_options.domains = context.actions.account.formatDomains(
      newParams.widget_options.domains,
    );
  }
  return { firstErrorMsg, newParams, fieldErrors };
};

export const validateAffiliateConfig = (
  context: Context,
  { affiliateConfigArgs, fieldErrors }: { affiliateConfigArgs: any; fieldErrors: any },
) => {
  const { defaultAffiliate, linkmateAffiliate } = affiliateConfigArgs;
  let configErr;

  if (
    affiliateConfigArgs.hasOwnProperty(defaultAffiliate) &&
    !affiliateConfigArgs[defaultAffiliate]
  ) {
    configErr = `Missing information for ${defaultAffiliate}`;
    fieldErrors.push(defaultAffiliate.replace(/ /g, '-').toLowerCase());
  } else if (
    affiliateConfigArgs.hasOwnProperty(linkmateAffiliate) &&
    !affiliateConfigArgs[linkmateAffiliate]
  ) {
    configErr = `Missing information for ${linkmateAffiliate}`;
    fieldErrors.push(linkmateAffiliate.replace(/ /g, '-').toLowerCase());
  }

  const defAffPayload = context.actions.account.getDefaultAndLinkmateAffiliateParams({
    key: defaultAffiliate,
    affiliateConfigArgs,
  });
  const linkAffPayload = context.actions.account.getDefaultAndLinkmateAffiliateParams({
    key: linkmateAffiliate,
    affiliateConfigArgs,
  });

  return [configErr, { ...defAffPayload, ...linkAffPayload }];
};

export const getDefaultAndLinkmateAffiliateParams = (
  context: Context,
  { key, affiliateConfigArgs }: { key: any; affiliateConfigArgs: any },
) => {
  const value = affiliateConfigArgs[key];
  const paramMap = {
    Amazon: { amazon_config: { id: value } },
    'Impact Radius': { impact_radius_config: { tracking_url: value } },
    Linkshare: { linkshare_config: { member_id: value } },
    PMG: { pmg_config: { tracking_url: value } },
    Skimlinks: { skimlinks_config: { id: value } },
    Viglink: { viglink_config: { key: value } },
  };

  return paramMap[key] || {};
};

export const removeProtocol = (context: Context, domain: string) => {
  // To strip protocol and trailing slash if any
  return domain.replace(/^https?:\/\/?/, '').replace(/\/$/, '');
};

export const formatDomains = (context: Context, domains: string[]) => {
  // Remove all empty domains
  let newDomains = _.cloneDeep(domains);
  newDomains = newDomains.filter((domain: any) => domain);

  newDomains.forEach((domain: any) => {
    const inputDomain = context.actions.account.removeProtocol(domain);
    if (domainRegex.exec((inputDomain as unknown) as string)) {
      domain = inputDomain;
    }
  });

  // Remove all duplicate domains
  newDomains = newDomains.filter(
    (domain: any, index: any, domains: any) => domains.indexOf(domain) === index,
  );

  return newDomains;
};

export const checkDomains = (context: Context, { param, value }: { param: any; value: any }) => {
  let errorMsg = '';
  value.forEach((domain: any) => {
    const inputDomain = context.actions.account.removeProtocol(domain);
    if (!domainRegex.exec((inputDomain as unknown) as string)) {
      errorMsg = `Invalid domain name '${inputDomain}'`;
    }
  });

  return errorMsg;
};

export const checkNonEmpty = (context: Context, { param, value }: { param: any; value: any }) => {
  let errorMsg;

  const requiredParams = [
    'full_name',
    'short_name',
    'url',
    'cogs',
    'email',
    'first_name',
    'last_name',
    'invoice_recipient',
  ];

  if (requiredParams.includes(param) && !value && typeof value === 'string') {
    errorMsg = 'Please complete all required fields.';
  }

  return errorMsg;
};

export const checkLength = (
  context: Context,
  { param, value }: { param: any; value: any },
): any => {
  let errorMsg;

  const maxLengths = {
    short_name: 49,
    first_name: 100,
    last_name: 100,
    email: 255,
  };

  if (Object.keys(maxLengths).includes(param) && value.length > maxLengths[param]) {
    const formattedParam = param.replace(/_/g, ' ');
    errorMsg = `${titleCase(formattedParam)} must be less than ${maxLengths[param]} characters.`;
  }

  return errorMsg;
};

export const sortByName = (context: Context, orgs: { short_name: string }[]) => {
  const newOrgs = _.cloneDeep(orgs);

  return newOrgs.sort((a: any, b: any) =>
    a.short_name.toLowerCase() > b.short_name.toLowerCase() ? 1 : -1,
  );
};
